import React, {Fragment, SyntheticEvent, useCallback, useContext, useState} from 'react';
import b_ from 'b_';
import {useDispatch, useSelector} from 'react-redux';

import {
    useAudienceCategories,
    useAudienceInterests,
    useAudienceSegments
} from 'client/common/hooks/audience';
import {useIsCreativeAssessor} from 'client/common/hooks/user';
import {useDevice} from 'client/common/hooks/device';
import ListModeContext from 'client/common/contexts/list-mode';

import {Button, Spin} from 'client/common/components/lego';
import ClientSelect from 'client/common/components/client-select';
import SurveyStateFilter from 'client/quicksurvey/list/components/survey-state-filter';
import SurveyNameFilter from 'client/quicksurvey/list/components/survey-name-filter';

import {ListMode} from 'client/common/store/reducers/surveys/types';
import {
    changeClientIdFilter,
    resetFilter,
    fetchSurveyList
} from 'client/common/store/reducers/surveys';
import {
    getIsDefaultFilter,
    getIsEntirelyLoaded,
    getIsLoadingSurveysList,
    getSurveyFilter,
    getSurveyList
} from 'client/common/store/selectors/surveys';

import {Device} from 'client/common/utils/device-width';

import I18N from 'assets/i18n/brandlift-project';

import TumblerList from './icons/tumbler-list.svg';
import TumblerGrid from './icons/tumbler-grid.svg';

import Grid from './grid';
import Table from './table';

import {ListView} from './types';

import './survey-list.css';

const i18n = I18N.list.surveyList;
const b = b_.with('survey-list');

function ViewTumbler({onClick}: {onClick: (view: ListView) => void}) {
    function handleClick(e: SyntheticEvent<HTMLDivElement>) {
        onClick((e.currentTarget.dataset.value as ListView) ?? ListView.Grid);
    }

    return (
        <div className={b('tumbler')}>
            <img
                className={b('tumbler-icon')}
                src={TumblerGrid}
                alt={i18n.view.grid}
                data-value={ListView.Grid}
                onClick={handleClick}
            />
            <img
                className={b('tumbler-icon')}
                src={TumblerList}
                alt={i18n.view.list}
                data-value={ListView.Table}
                onClick={handleClick}
            />
        </div>
    );
}

function EmptySurveyListMessage() {
    return <p className={b('empty-list')}>{i18n.surveysNotFound}</p>;
}

function AgencyPanel() {
    const filter = useSelector(getSurveyFilter);

    const dispatch = useDispatch();

    const handleClientSelectChange = useCallback(
        (clientId?: string) => {
            dispatch(changeClientIdFilter({clientId}));
        },
        [dispatch]
    );

    return (
        <div className={b('agency-panel')}>
            <ClientSelect onChange={handleClientSelectChange} selectedId={filter.clientId} />
        </div>
    );
}

// eslint-disable-next-line complexity, max-statements
export function FilledState() {
    const list = useSelector(getSurveyList);
    const isDefaultFilter = useSelector(getIsDefaultFilter);
    const isEntirelyLoaded = useSelector(getIsEntirelyLoaded);
    const isLoadingSurveysList = useSelector(getIsLoadingSurveysList);

    const device = useDevice();
    const listMode = useContext(ListModeContext);

    const [listView, setListView] = useState(ListView.Table);

    const categories = useAudienceCategories();
    const interests = useAudienceInterests();
    const segments = useAudienceSegments();

    const isCreativeAssessor = useIsCreativeAssessor();
    const dispatch = useDispatch();

    const loadMore = useCallback(() => {
        if (!isLoadingSurveysList) {
            dispatch(fetchSurveyList({isCreativeAssessor}));
        }
    }, [dispatch, isCreativeAssessor, isLoadingSurveysList]);

    const handleClickShowAll = useCallback(() => dispatch(resetFilter()), [dispatch]);

    const isTableAvailable = [Device.Desktop, Device.Widescreen].includes(device);

    const SurveyListComponent = isTableAvailable && listView === ListView.Table ? Table : Grid;

    let ListComponent = <Spin size='xs' view='default' position='center' progress />;

    // переживаем ошибки от апи аудиторий
    const isTargetingDataLoaded =
        categories.status === 'success' && interests.status === 'success' && !segments.isFetching;

    if (isTargetingDataLoaded) {
        ListComponent =
            list.length || !isEntirelyLoaded ? (
                <SurveyListComponent
                    list={list}
                    loadMore={loadMore}
                    categories={categories.data}
                    interests={interests.data}
                    segments={segments.data || []}
                    isEntirelyLoaded={isEntirelyLoaded}
                />
            ) : (
                <EmptySurveyListMessage />
            );
    }

    return (
        <Fragment>
            <div className={b('header')}>
                <h2 className={b('title')}>{i18n.title}</h2>
                {isTableAvailable && <ViewTumbler onClick={setListView} />}
                <SurveyNameFilter mix={b('survey-name-filter')} />
                <SurveyStateFilter mix={b('survey-state-filter')} />
            </div>
            {listMode === ListMode.Agency && <AgencyPanel />}
            {ListComponent}
            {!isDefaultFilter && (
                <Button
                    className={b('button', {
                        type: 'show-all'
                    })}
                    view='default'
                    size='m'
                    onClick={handleClickShowAll}
                >
                    {i18n.showAllSurveys}
                </Button>
            )}
        </Fragment>
    );
}
